/**
 * MeetingSidePanel — the panel that sits beside MeetingStage in both Live
 * Interview rooms.
 *
 * Always collapsible (a button tucks it away so the video stage gets the
 * full width, and a rail button brings it back). Optionally resizable, via a
 * drag handle on its left edge — both rooms turn this on now, since how much
 * room the panel needs (e.g. a wide code editor question) is a per-person
 * preference either side of the call can have.
 *
 * Dragging is wired on the window (pointer capture via window listeners
 * attached on pointerdown, removed on pointerup) rather than on the handle
 * element, so a fast drag that outruns the pointer doesn't drop the gesture
 * the moment the cursor leaves the 6px handle.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronDoubleRightIcon, ChevronDoubleLeftIcon } from '@heroicons/react/24/outline'

// All three sized as a share of the viewport, not fixed pixels — a fixed
// pixel width doesn't scale with the window (on a wide screen it settles
// well short of the intended default/range; on a narrow one it can eat the
// whole video stage). Default 60/40 (video/panel), draggable between 70/30
// and 30/70.
const DEFAULT_WIDTH_RATIO = 0.4
const MIN_WIDTH_RATIO = 0.3
const MAX_WIDTH_RATIO = 0.7
// Absolute floor so the panel never gets unusably thin on a very narrow
// window, where 30% would only be a couple hundred pixels.
const ABSOLUTE_MIN_WIDTH = 280

function clampWidth(px: number): number {
  const min = Math.max(ABSOLUTE_MIN_WIDTH, window.innerWidth * MIN_WIDTH_RATIO)
  const max = Math.max(min, window.innerWidth * MAX_WIDTH_RATIO)
  return Math.min(max, Math.max(min, px))
}

interface MeetingSidePanelTab {
  key: string
  label: string
  /** A count badge next to the label (e.g. unread chat messages) — omitted/0 shows nothing. */
  badge?: number
  /** `data-tour` anchor for a guided-tour spotlight (see LiveInterviewRoomPage's
   * LiveInterviewTour) — omitted for callers with no tour, so this never
   * shows up as a stray attribute on the recruiter room's tabs. */
  dataTour?: string
}

interface MeetingSidePanelProps {
  open: boolean
  onToggle: () => void
  /** Enables the drag-to-resize handle. Off by default. */
  resizable?: boolean
  /** Starting width, as a share of the viewport. Defaults to DEFAULT_WIDTH_RATIO
   * (0.4 — the recruiter room's 60/40 video/panel split). The candidate room
   * passes 0.7 for a 30/70 video/panel default, since its panel is where the
   * candidate actually reads and answers the question — still draggable
   * between the same MIN/MAX_WIDTH_RATIO bounds as every other room. */
  defaultWidthRatio?: number
  title?: string
  /** When provided, renders a tab strip below the header — Interview vs.
   * Chat live in their own tabs instead of one long scrolling column, so
   * switching to Chat is a real "viewed it" moment (see the badge count,
   * which the caller is responsible for zeroing on tab change). */
  tabs?: MeetingSidePanelTab[]
  activeTab?: string
  onTabChange?: (key: string) => void
  children: React.ReactNode
}

export default function MeetingSidePanel({
  open, onToggle, resizable = false, defaultWidthRatio = DEFAULT_WIDTH_RATIO, title = 'Interview', tabs, activeTab, onTabChange, children,
}: MeetingSidePanelProps) {
  const [width, setWidth] = useState(() => clampWidth(window.innerWidth * defaultWidthRatio))
  const draggingRef = useRef(false)

  const onPointerDown = useCallback((event: React.PointerEvent) => {
    if (!resizable) return
    event.preventDefault()
    draggingRef.current = true
  }, [resizable])

  useEffect(() => {
    if (!resizable) return
    const onMove = (event: PointerEvent) => {
      if (!draggingRef.current) return
      // The panel is pinned to the right edge, so its width is simply the
      // distance from the pointer to that edge.
      const next = window.innerWidth - event.clientX
      setWidth(clampWidth(next))
    }
    const onUp = () => { draggingRef.current = false }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [resizable])

  if (!open) {
    return (
      <button
        onClick={onToggle}
        title={`Show ${title.toLowerCase()} panel`}
        aria-label={`Show ${title.toLowerCase()} panel`}
        className="flex-shrink-0 self-stretch px-1.5 bg-gray-950/80 border-l border-white/10 text-gray-300 hover:text-white hover:bg-gray-900"
      >
        <ChevronDoubleLeftIcon className="w-4 h-4" />
      </button>
    )
  }

  return (
    <div className="flex flex-shrink-0 min-h-0" style={{ width }}>
      {resizable && (
        <div
          onPointerDown={onPointerDown}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          className="w-1.5 flex-shrink-0 cursor-col-resize bg-white/5 hover:bg-blue-500/60 transition-colors"
        />
      )}
      {/* The body sits on a light ground rather than plain white so the
          cards each room stacks into it (question, scoring, notes, quota,
          chat) read as distinct surfaces instead of one long divider-
          separated column. Purely visual — no consumer's props or bindings
          changed. */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col bg-gray-50 border-l border-gray-200">
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
          <h2 className="text-sm font-semibold text-gray-900 truncate tracking-tight">{title}</h2>
          <button
            onClick={onToggle}
            title={`Hide ${title.toLowerCase()} panel`}
            aria-label={`Hide ${title.toLowerCase()} panel`}
            className="p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <ChevronDoubleRightIcon className="w-4 h-4" />
          </button>
        </div>
        {tabs && tabs.length > 0 && (
          <div className="flex-shrink-0 flex items-center gap-1 px-2 pt-2 bg-white border-b border-gray-200">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                data-tour={tab.dataTour}
                onClick={() => onTabChange?.(tab.key)}
                className={`relative inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-md border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-purple-600 text-purple-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {!!tab.badge && (
                  <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none">
                    {tab.badge > 9 ? '9+' : tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">{children}</div>
      </div>
    </div>
  )
}
